import React from 'react';
import uniqueId from 'lodash.uniqueid';

import Icon from '~/components/icon';

import styles from './TextInput.module.css';

export interface TextInputProps {
  errorMessage?: string;
  hasError: boolean;
  inputClassName?: string;
  isRequired?: boolean;
  isValid?: boolean;
  labelName?: string;
  name: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  shouldAutocomplete?: boolean;
  shouldUseUniqueId?: boolean;
  type: string;
  value?: string;
  wrapperClassName?: string;
}

const TextInput: React.FC<TextInputProps> = ({
  hasError = false,
  inputClassName,
  isRequired = false,
  isValid,
  labelName,
  name,
  onBlur,
  onChange,
  placeholder = '',
  shouldAutocomplete = true,
  shouldUseUniqueId,
  type,
  value,
  wrapperClassName,
  ...otherProps
}) => {
  const [inputValue, setInputValue] = React.useState(value);

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  /**
   * Function to handle input change
   * @param event
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(event.target.value);
    if (onChange) {
      onChange(event);
    }
  };

  /**
   * Function to handle input blur
   * @param event
   */
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
    if (onBlur) {
      onBlur(event);
    }
  };

  /**
   * Renders Input label
   */
  const renderLabel = (): JSX.Element | null => {
    return !!type && !!labelName ?
    <div className={styles.labelWrapper}>
				<label className={styles.label} htmlFor={type}>
					{labelName}
				</label>
				{isRequired && <span className={styles.asterisk}>*</span>}
			</div> :
    null;
  };

  /**
   * Render Valid or Error Icon
   */
  const renderIcon = (): JSX.Element | null => {
    return isValid || hasError ?
    <div className={styles.icon}>
				{hasError ?
      <Icon height={20} icon="ErrorCircle" width={20} /> :

      <div className={styles.checkmark}>
						<Icon fill="#fff" height={20} icon="Checkmark" width={20} />
					</div>
      }
			</div> :
    null;
  };

  const autocomplete = type === 'password' ? 'current-password' : type;
  const classNames = `${styles.input} ${hasError ? styles.error : ''} ${
  isValid ? styles.valid : ''} ${
  inputClassName}`;

  return (
    <div className={styles.textInput}>
			{renderLabel()}
			<div className={`${styles.textInputWrapper} ${wrapperClassName}`}>
				<input
          autoComplete={shouldAutocomplete ? autocomplete : 'off'}
          className={classNames}
          id={shouldUseUniqueId ? uniqueId(`${name}_`) : name}
          name={name}
          onBlur={handleBlur}
          onChange={handleChange}
          placeholder={placeholder}
          type={type}
          value={inputValue || ''}
          {...otherProps} />

				{renderIcon()}
			</div>
		</div>);

};

export default TextInput;